import React from 'react';
import { Link } from 'react-router-dom';
import SEO from './SEO';
import Section from './ui/Section';
import Callout from './ui/Callout';
import Accordion from './ui/Accordion';
import ContactForm from './ui/ContactForm';
import { generateSupportSEO, generateFAQSchema } from '../utils/seo';
import { APP_STORE_ROUTE } from '../constants/urls';
import '../styles/Support.css';

const Support = () => {
  const faqItems = [
    {
      question: "Do I need to link my bank accounts?",
      answer: "No. Lown works without third-party aggregators. You input your details manually to stay in control of your data."
    },
    {
      question: "Can I track multiple loans?",
      answer: "Yes. Add and manage multiple saved loans including mortgages, auto, or personal."
    },
    {
      question: "Does Lown work offline?",
      answer: "Yes. Calculators and saved data work offline."
    },
    {
      question: "How accurate are the calculations?",
      answer: "Lown uses industry-standard formulas like amortization schedules and DTI ratios. Actual lender terms may vary."
    }
  ];

  const troubleshootingItems = [
    {
      issue: "App will not open",
      solution: "Force close and reopen. If needed, restart your device."
    },
    {
      issue: "Data not saving",
      solution: "Update to the latest version and confirm device storage."
    },
    {
      issue: "Numbers look off",
      solution: "Recheck inputs like interest rate, term, and extra payments."
    }
  ];

  const seoData = generateSupportSEO();
  const faqSchema = generateFAQSchema();

  return (
    <>
      <SEO 
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        url={seoData.url}
        structuredData={faqSchema}
      />
      
      <div className="support-page">
        {/* Hero Section */}
        <section className="support-hero">
          <div className="support-container animate-fade-in-up">
            <h1 className="support-title">Lown Support</h1>
            <p className="support-subtitle">We're here to help you get the most out of Lown: Loan to Own.</p>
          </div>
        </section>

        {/* Getting Started Section */}
        <Section 
          title="Getting Started"
          className="getting-started-section"
        >
          <div className="getting-started-content">
            <ul className="getting-started-list">
              <li>
                <Link to={APP_STORE_ROUTE}>Install Lown from the App Store</Link> and explore your loan and affordability tools.
              </li>
              <li>Start with the Loan Calculator or Affordability Check to understand your baseline.</li>
              <li>Privacy: Lown does not sell or share your personal financial information. Your data stays on your device unless you export or back it up.</li>
            </ul>
          </div>
        </Section>

        {/* FAQ Section */}
        <Section 
          title="Frequently Asked Questions"
          className="faq-section"
        >
          <div className="faq-container">
            <Accordion items={faqItems} className="faq-accordion" />
          </div>
        </Section>

        {/* Troubleshooting Section */}
        <Section 
          title="Troubleshooting"
          className="troubleshooting-section"
        >
          <div className="troubleshooting-content">
            <div className="troubleshooting-grid">
              {troubleshootingItems.map((item, index) => (
                <div key={index} className="troubleshooting-item">
                  <h3 className="troubleshooting-issue">{item.issue}</h3>
                  <p className="troubleshooting-solution">{item.solution}</p>
                </div>
              ))}
            </div>
          </div>
        </Section>

        {/* Contact Us Section */}
        <Section 
          title="Contact Us"
          className="contact-section"
        >
          <div className="contact-content">
            <div className="contact-info">
              <p>If you cannot find your answer, reach out.</p>
              <div className="contact-methods">
                <div className="contact-method">
                  <strong>Email:</strong> <a href="mailto:support@lown.app">support@lown.app</a>
                </div>
                <div className="contact-method">
                  <strong>In-App:</strong> Settings → Help & Feedback
                </div>
              </div>
              <Callout variant="info" className="response-time-callout">
                We typically respond within 24–48 hours.
              </Callout>
            </div>
            
            <div className="contact-form-wrapper">
              <ContactForm />
            </div>
          </div>
        </Section>

      </div>
    </>
  );
};

export default Support;
